import type { Role } from '@crm/shared'
import { apiClient } from './client'

/** Роль аккаунта — общий список ролей из @crm/shared (с ролями этапа 3). */
export type UserRoleName = Role

export interface UserAccount {
  id: string
  email: string
  name: string | null
  role: UserRoleName
  isBlocked: boolean
  cities: string[]
  citiesExcludeMode: boolean
  /** Только для роли ASSISTANT (Помощник): id прикреплённых менеджеров. */
  assistantManagerIds: string[]
  createdAt: string
  updatedAt: string
}

export async function getUsers(): Promise<UserAccount[]> {
  const res = await apiClient.get<{ success: true; data: UserAccount[] }>('/users')
  return res.data.data
}

export async function createUser(data: {
  email: string
  password: string
  name?: string
  role: UserRoleName
}): Promise<UserAccount> {
  const res = await apiClient.post<{ success: true; data: UserAccount }>('/users', data)
  return res.data.data
}

export async function updateUser(
  id: string,
  data: {
    email?: string
    password?: string
    name?: string | null
    role?: UserRoleName
  },
): Promise<UserAccount> {
  const res = await apiClient.patch<{ success: true; data: UserAccount }>(`/users/${id}`, data)
  return res.data.data
}

export interface DeletePreview {
  user: { id: string; email: string; name: string | null; role: UserRoleName }
  counts: {
    clients: number
    requests: number
    quotes: number
    quotations: number
    comments: number
    statusHistory: number
    itemStatusHistory: number
    requestEvents: number
  }
  needsTransfer: boolean
  canDelete: boolean
  blockReason: string | null
  candidates: Array<{ id: string; email: string; name: string | null; role: UserRoleName }>
}

export interface DeleteResult {
  deletedUserId: string
  transferredTo: string | null
  transferredClients: number
  transferredRequests: number
  orphaned: {
    quotes: number
    quotations: number
    comments: number
    statusHistory: number
    itemStatusHistory: number
    requestEvents: number
  }
}

export async function getUserDeletePreview(id: string): Promise<DeletePreview> {
  const res = await apiClient.get<{ success: true; data: DeletePreview }>(
    `/users/${id}/delete-preview`,
  )
  return res.data.data
}

export async function deleteUser(id: string, transferTo?: string): Promise<DeleteResult> {
  const res = await apiClient.delete<{ success: true; data: DeleteResult }>(`/users/${id}`, {
    data: transferTo ? { transferTo } : {},
  })
  return res.data.data
}

export async function updateUserCities(
  id: string,
  cities: string[],
  citiesExcludeMode?: boolean,
): Promise<UserAccount> {
  const payload: { cities: string[]; citiesExcludeMode?: boolean } = { cities }
  if (citiesExcludeMode !== undefined) payload.citiesExcludeMode = citiesExcludeMode
  const res = await apiClient.patch<{ success: true; data: UserAccount }>(`/users/${id}`, payload)
  return res.data.data
}

/** Привязка помощника к менеджерам (роль ASSISTANT). */
export async function updateAssistantManagers(
  id: string,
  assistantManagerIds: string[],
): Promise<UserAccount> {
  const res = await apiClient.patch<{ success: true; data: UserAccount }>(`/users/${id}`, {
    assistantManagerIds,
  })
  return res.data.data
}

export async function toggleBlockUser(id: string): Promise<UserAccount> {
  const res = await apiClient.patch<{ success: true; data: UserAccount }>(`/users/${id}/block`)
  return res.data.data
}

export interface ProcurementUser {
  id: string
  email: string
  name: string | null
}

export async function getProcurementUsers(): Promise<ProcurementUser[]> {
  const res = await apiClient.get<{ success: true; data: ProcurementUser[] }>('/users/procurement')
  return res.data.data
}

/** Кого можно назначить на позицию (выпадашки «Назначен» / «Назначить…»). */
export interface AssignableUser extends ProcurementUser {
  role: 'PROCUREMENT' | 'ROS'
}

/**
 * Закупщики и РОЗ для назначения в карточке заявки (№50, 29.09.2026). Для РОЗ
 * сервер отдаёт её саму и её команду. `/users/procurement` — отдельный список
 * для «Таблицы» и проектов, в нём РОЗ нет.
 */
export async function getAssignableBuyers(): Promise<AssignableUser[]> {
  const res = await apiClient.get<{ success: true; data: AssignableUser[] }>(
    '/users/assignable-buyers',
  )
  return res.data.data
}
